import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, CreditCard as Edit, ExternalLink, AlertTriangle, Bot, PlusCircle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { Link, useNavigate } from 'react-router-dom';
import { useBots } from '@/contexts/BotsContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { toast } from 'sonner';

const sortData = <T extends { [key: string]: any }>(data: T[], config: { key: string; direction: 'asc' | 'desc' | null }) => {
  if (!config.key || !config.direction) return data;

  return [...data].sort((a, b) => {
    const aValue = a[config.key];
    const bValue = b[config.key];

    if (aValue === bValue) return 0;

    if (config.direction === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
};

export const ActiveBotsPage: React.FC = () => {
  const { activeBots, toggleBotRunning, editBot, clearCurrentBot, setPracticeMode } = useBots();
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc' | null;
  }>({ key: '', direction: null });
  const [practiceSortConfig, setPracticeSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc' | null;
  }>({ key: '', direction: null });
  const [loading, setLoading] = useState<string | null>(null);

  const handleCreateNewBot = (isPractice = false) => {
    clearCurrentBot();
    setPracticeMode(isPractice);
    navigate('/create-bot');
  };

  const handleSort = (key: string, isPractice = false) => {
    const setConfig = isPractice ? setPracticeSortConfig : setSortConfig;
    const currentConfig = isPractice ? practiceSortConfig : sortConfig;
    
    let direction: 'asc' | 'desc' | null = 'asc';

    if (currentConfig.key === key) {
      if (currentConfig.direction === 'asc') {
        direction = 'desc';
      } else if (currentConfig.direction === 'desc') {
        direction = null;
      }
    }

    setConfig({ key, direction });
  };

  const handleToggleBotRunning = async (botId: string, isPractice = false) => {
    setLoading(botId);

    try {
      await toggleBotRunning(botId);
      toast.success('Bot trading status updated');
    } catch (error) {
      console.error('Error toggling bot running:', error);
      toast.error('Failed to toggle bot trading status');
    }

    setLoading(null);
  };

  const handleEditBot = (botId: string, isPractice = false) => {
    editBot(botId, isPractice);
    setPracticeMode(isPractice);
    navigate('/create-bot');
  };

  const getSortIcon = (key: string, isPractice = false) => {
    const config = isPractice ? practiceSortConfig : sortConfig;
    
    if (config.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1" />;
    }
    
    if (config.direction === 'asc') {
      return <ArrowUp className="h-4 w-4 ml-1" />;
    }
    
    if (config.direction === 'desc') {
      return <ArrowDown className="h-4 w-4 ml-1" />;
    }

    return <ArrowUpDown className="h-4 w-4 ml-1" />;
  };

  const getBotStatusColor = (bot: any) => {
    if (!bot.isConnected) {
      return {
        bg: 'bg-orange-100 dark:bg-orange-900/20',
        text: 'text-orange-800 dark:text-orange-200',
        label: 'Error'
      };
    }

    // ✅ Check is_running - bot is trading
    if (bot.is_running) {
      return {
        bg: 'bg-green-100 dark:bg-green-900/20',
        text: 'text-green-800 dark:text-green-200',
        label: 'Active'
      };
    }

    // ✅ Bot is in Active Bots but not running
    return {
      bg: 'bg-gray-100 dark:bg-gray-900/20',
      text: 'text-gray-800 dark:text-gray-200',
      label: 'Inactive'
    };
  };

  // ✅ Split activeBots by deployment_target
  const activeLiveBots = activeBots.filter(bot => bot.deploymentTarget === 'live');
  const activePracticeBots = activeBots.filter(bot => bot.deploymentTarget === 'paper');

  const sortedBots = sortData(activeLiveBots, sortConfig);
  const sortedPracticeBots = sortData(activePracticeBots, practiceSortConfig);

  const SortableHeader: React.FC<{
    label: string;
    sortKey: string;
    isPractice?: boolean;
  }> = ({ label, sortKey, isPractice = false }) => (
    <th 
      scope="col" 
      className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors duration-200"
      onClick={() => handleSort(sortKey, isPractice)}
    >
      <div className="flex items-center justify-between">
        <span>{label}</span>
        <span className="ml-1">
          {getSortIcon(sortKey, isPractice)}
        </span>
      </div>
    </th>
  );

  const renderBotRow = (bot: typeof bots[0], isPractice = false) => {
    const status = getBotStatusColor(bot);
    
    return (
      <tr key={bot.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
        <td className="px-2 py-2 whitespace-nowrap">
          <div className="flex items-center">
            <div className="h-6 w-6 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <Bot className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">{bot.name}</span>
          </div>
        </td>
        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{bot.exchange}</td>
        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{bot.strategy}</td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
            {!bot.isConnected && <AlertTriangle className="w-3 h-3 mr-1" />}
            {status.label}
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {bot.totalTrades}
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span className={`text-sm font-medium ${
            bot.winRate >= 60 
              ? 'text-green-600 dark:text-green-400' 
              : bot.winRate >= 50
                ? 'text-yellow-600 dark:text-yellow-400'
                : 'text-red-600 dark:text-red-400'
          }`}>
            {bot.winRate}%
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap">
          <span className={`text-sm font-medium ${
            bot.profit >= 0 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'
          }`}>
            {bot.profit >= 0 ? '+' : ''}{bot.profit}%
          </span>
        </td>
        <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          {bot.updated_at}
        </td>
        <td className="px-2 py-2 whitespace-nowrap text-right">
          <div className="flex justify-end space-x-1">
            <Link 
              to={`/bot/${bot.id}`}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <ExternalLink className="h-4 w-4" />
            </Link>
            <button 
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              onClick={() => handleEditBot(bot.id, isPractice)}
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              className={`${
                bot.is_running
                  ? 'text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300'
                  : 'text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300'
              }`}
              onClick={() => handleToggleBotRunning(bot.id, isPractice)}
              disabled={loading === bot.id}
            >
              {loading === bot.id ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : bot.is_running ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <DashboardLayout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-6"
      >
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Active Bots</h2>
                <Button 
                  variant="primary" 
                  size="sm"
                  icon={<PlusCircle size={16} />}
                  iconPosition="left"
                  onClick={() => handleCreateNewBot(false)}
                >
                  Create New Bot
                </Button>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <SortableHeader label="Bot Name" sortKey="name" />
                        <SortableHeader label="Exchange" sortKey="exchange" />
                        <SortableHeader label="Strategy" sortKey="strategy" />
                        <SortableHeader label="Status" sortKey="isActive" />
                        <SortableHeader label="Total Trades" sortKey="totalTrades" />
                        <SortableHeader label="Win Rate" sortKey="winRate" />
                        <SortableHeader label="Profit" sortKey="profit" />
                        <SortableHeader label="Last Updated" sortKey="lastUpdated" />
                        <th scope="col" className="px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedBots.map(bot => renderBotRow(bot, false))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Practice Bots Section */}
              <div className="mt-12">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Practice Bots</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Test your strategies risk-free with paper trading
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    icon={<PlusCircle size={16} />}
                    iconPosition="left"
                    onClick={() => handleCreateNewBot(true)}
                  >
                    New Practice Bot
                  </Button>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <SortableHeader label="Bot Name" sortKey="name" isPractice={true} />
                          <SortableHeader label="Exchange" sortKey="exchange" isPractice={true} />
                          <SortableHeader label="Strategy" sortKey="strategy" isPractice={true} />
                          <SortableHeader label="Status" sortKey="isActive" isPractice={true} />
                          <SortableHeader label="Total Trades" sortKey="totalTrades" isPractice={true} />
                          <SortableHeader label="Win Rate" sortKey="winRate" isPractice={true} />
                          <SortableHeader label="Profit" sortKey="profit" isPractice={true} />
                          <SortableHeader label="Last Updated" sortKey="lastUpdated" isPractice={true} />
                          <th scope="col" className="px-2 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedPracticeBots.map(bot => renderBotRow(bot, true))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
      </motion.div>
    </DashboardLayout>
  );
};

export default ActiveBotsPage;